import React, { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { adminAuthService } from "@/api/adminAuthService";
import { Shield } from "lucide-react";

/**
 * ============================================================
 * PROTECTED ADMIN ROUTE GUARD
 * ============================================================
 * Intercepts navigation to administrative paths.
 * Validates active Supabase session and executes `public.is_admin()`
 * authorization check on mount and session changes.
 */
export default function ProtectedAdminRoute() {
  const location = useLocation();
  const [authState, setAuthState] = useState({
    loading: true,
    isAdmin: adminAuthService.isAuthenticated(),
  });

  useEffect(() => {
    let mounted = true;

    adminAuthService.checkAuth().then((adminUser) => {
      if (mounted) {
        setAuthState({
          loading: false,
          isAdmin: Boolean(adminUser),
        });
      }
    });

    const { data: authListener } = adminAuthService.onAuthStateChange((adminUser) => {
      if (mounted) {
        setAuthState({
          loading: false,
          isAdmin: Boolean(adminUser),
        });
      }
    });

    return () => {
      mounted = false;
      if (authListener?.subscription?.unsubscribe) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []);

  if (authState.loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-4 text-slate-900">
        <div className="w-12 h-12 rounded-2xl bg-cyan-50 border border-cyan-200 flex items-center justify-center animate-pulse shadow-sm">
          <Shield className="w-6 h-6 text-cyan-600" />
        </div>
        <div className="text-xs text-slate-500 font-medium tracking-wider uppercase font-mono">
          Verifying Administrator Privileges...
        </div>
      </div>
    );
  }

  if (!authState.isAdmin) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
}
